// Shared marketing top bar — sticky, hairline-bordered, novem language
// (mockup market/nav-states.jsx _NavShell dropdowns + heroes.jsx MarketNav).
// Visual layer only; no auth, no data.
//
// D2 adds accessible hover/focus mega-menus for Product, Modules and
// Resources (mockup NNavUrun / NNavModuller / NNavKaynaklar). The remaining
// items (Novem AI, Pricing) stay plain route links. Each mega-menu trigger is
// a real <button> with aria-expanded + aria-controls pointing at its panel;
// the panel opens on pointer hover AND keyboard focus-within, closes on
// Escape, on blur leaving the group, and when a link inside is followed. The
// mobile disclosure menu (lg:hidden) is unchanged — the mega-menus are
// desktop-only (the mobile menu lists the same routes flat).
//
// "Giriş yap" points at the customer-web app root (a separate app, absolute
// ingress URL); "Demo iste" opens the contact form with the demo topic
// preselected (there is no separate demo-booking contract).

import { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  ChevronDown,
  HelpCircle,
  Inbox,
  Mail,
  Menu,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Target,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router";

import { Button } from "@novemai/components";

import { Wordmark } from "@/components/Wordmark";
import { DEMO_REQUEST_PATH } from "@/lib/site-links";
import { cn } from "@/lib/utils";

/**
 * Resolve the separately deployed customer app ingress without ever falling
 * back to a path on this marketing SPA. A missing/unsafe deployment value
 * hides the CTA rather than sending a visitor to a broken relative route.
 */
export function resolveCustomerAppLoginUrl(configured: string | undefined): string | undefined {
  const candidate = configured?.trim();
  if (candidate === undefined || candidate.length === 0) return undefined;

  try {
    const url = new URL(candidate);
    if (
      (url.protocol !== "http:" && url.protocol !== "https:") ||
      url.username !== "" ||
      url.password !== "" ||
      url.search !== "" ||
      url.hash !== ""
    ) {
      return undefined;
    }
    return url.toString();
  } catch {
    return undefined;
  }
}

const APP_LOGIN_URL = resolveCustomerAppLoginUrl(
  import.meta.env["VITE_APP_LOGIN_URL"] as string | undefined,
);

// ── Menu data ───────────────────────────────────────────────────────
type MenuKey = "product" | "modules" | "resources";

interface IconLink {
  readonly key: string;
  readonly Icon: LucideIcon;
  readonly to: string;
}

// Each product link resolves to its per-module detail page (/modules/<key>,
// see module-detail.tsx) — the keys are valid module slugs.
const PRODUCT_LINKS: readonly IconLink[] = [
  { key: "requests", Icon: Inbox, to: "/modules/requests" },
  { key: "leave", Icon: CalendarDays, to: "/modules/leave" },
  { key: "payroll", Icon: Wallet, to: "/modules/payroll" },
  { key: "org", Icon: Target, to: "/modules/org" },
  { key: "attendance", Icon: Smartphone, to: "/modules/attendance" },
  { key: "reports", Icon: BarChart3, to: "/modules/reports" },
];

interface ModuleTile {
  readonly key: string;
  readonly Icon: LucideIcon;
  readonly ai: boolean;
}
// `ai` marks only modules where a shipped AI capability exists: the natural
// language report (reports). Requests and payroll carried the flag for triage
// and the payroll explainer, neither of which is built.
const MODULE_TILES: readonly ModuleTile[] = [
  { key: "requests", Icon: Inbox, ai: false },
  { key: "leave", Icon: CalendarDays, ai: false },
  { key: "payroll", Icon: Wallet, ai: false },
  { key: "org", Icon: Target, ai: false },
  { key: "attendance", Icon: Smartphone, ai: false },
  { key: "reports", Icon: BarChart3, ai: true },
];

// Only surfaces that actually exist are linked. Security lands at the existing
// privacy document's security section (still visibly marked draft). Blog, Docs,
// case studies and Status are deferred until those surfaces ship
// (FRONTEND_PORT_MAP §10) — linking them now would dead-end at a placeholder.
const RESOURCE_LINKS: readonly IconLink[] = [
  { key: "help", Icon: HelpCircle, to: "/help" },
  { key: "contact", Icon: Mail, to: "/contact" },
  { key: "security", Icon: ShieldCheck, to: "/legal?doc=privacy#privacy-security" },
];

// ── Shared classes ──────────────────────────────────────────────────
const TRIGGER_CLASS = cn(
  "inline-flex items-center gap-1 rounded-[8px] px-2.5 py-2 font-sans text-sm font-medium",
  "text-[var(--fg-secondary)] transition-colors hover:bg-[var(--mkt-panel)]",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
);
const PANEL_LINK_CLASS = cn(
  "flex items-start gap-3 rounded-[10px] p-2.5 no-underline transition-colors hover:bg-[var(--mkt-panel)]",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
);

// ── A single mega-menu (trigger + panel), hover + keyboard aware ─────
function MegaMenu({
  menuKey,
  label,
  children,
}: {
  readonly menuKey: MenuKey;
  readonly label: string;
  readonly children: (close: () => void) => React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = `mega-${menuKey}`;

  const close = useCallback((): void => setOpen(false), []);

  // Close when focus leaves the whole group (Tab past the last link).
  const onBlur = useCallback((e: React.FocusEvent<HTMLDivElement>): void => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
  }, []);

  // Escape closes and returns focus to the trigger.
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      setOpen(false);
      const trigger = e.currentTarget.querySelector<HTMLButtonElement>("button[aria-controls]");
      trigger?.focus();
    }
  }, []);

  return (
    // The group coordinates open-state across the trigger + panel. It is NOT
    // itself an interactive control — the trigger <button> below carries the
    // role, aria-expanded/-controls, and click/keyboard semantics. Mouse
    // enter/leave are a pointer-only enhancement; keyboard users open via
    // focus (onFocus) and close via Escape (onKeyDown) / tabbing out (onBlur),
    // so the widget is fully operable without a pointer. The lint rule flags
    // the mouse handlers on a non-native element; the keyboard path makes this
    // safe.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="relative"
      onMouseEnter={(): void => setOpen(true)}
      onMouseLeave={(): void => setOpen(false)}
      onFocus={(): void => setOpen(true)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={open ? undefined : t("nav.mega.open_label", { label })}
        onClick={(): void => setOpen((v) => !v)}
        className={TRIGGER_CLASS}
      >
        {label}
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={cn("transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div id={panelId} className="absolute left-0 top-[calc(100%+10px)] z-50">
          {children(close)}
        </div>
      )}
    </div>
  );
}

// ── Product panel: featured AI card + platform links ────────────────
function ProductPanel({ close }: { readonly close: () => void }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="grid w-[680px] grid-cols-[240px_1fr] gap-5 rounded-[18px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-[22px] shadow-[var(--shadow-lg)]">
      {/* Featured AI card */}
      <Link
        to="/ai"
        onClick={close}
        className="flex flex-col rounded-[14px] bg-[var(--color-accent-950)] p-[22px] text-[var(--mkt-on-deep)] no-underline transition-opacity hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
      >
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-[var(--mkt-on-deep)]/10">
            <Sparkles size={17} aria-hidden="true" />
          </span>
          <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--color-accent-300)]">
            {t("nav.mega.product.featured.eyebrow")}
          </span>
        </div>
        <p className="mb-2.5 font-serif text-[18px] font-medium leading-snug">
          {t("nav.mega.product.featured.title")}
        </p>
        <p className="mb-[18px] font-serif text-[13px] leading-relaxed text-[var(--mkt-on-deep)]/65">
          {t("nav.mega.product.featured.body")}
        </p>
        <span className="mt-auto inline-flex items-center gap-1.5 font-sans text-[13px] font-semibold text-[var(--color-accent-300)]">
          {t("nav.mega.product.featured.cta")} <ArrowRight size={13} aria-hidden="true" />
        </span>
      </Link>

      {/* Platform links */}
      <div>
        <p className="mb-2.5 font-sans text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fg-muted)]">
          {t("nav.mega.product.platform_label")}
        </p>
        <ul className="flex flex-col gap-0.5">
          {PRODUCT_LINKS.map(({ key, Icon, to }) => (
            <li key={key}>
              <Link to={to} onClick={close} className={PANEL_LINK_CLASS}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[var(--mkt-panel)] text-[var(--fg-secondary)]">
                  <Icon size={15} aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-sans text-[13.5px] font-semibold text-[var(--fg-primary)]">
                    {t(`nav.mega.product.links.${key}.title`)}
                  </span>
                  <span className="block font-serif text-[12px] text-[var(--fg-muted)]">
                    {t(`nav.mega.product.links.${key}.desc`)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <Link
          to="/product"
          onClick={close}
          className="mt-2 inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1.5 font-sans text-[13px] font-semibold text-[var(--accent-hover)] no-underline hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        >
          {t("nav.mega.product.overview_link")} <ArrowRight size={13} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

// ── Modules panel: module tile grid ─────────────────────────────────
function ModulesPanel({ close }: { readonly close: () => void }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="w-[720px] rounded-[18px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-7 shadow-[var(--shadow-lg)]">
      <div className="mb-[18px] flex items-center justify-between">
        <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fg-muted)]">
          {t("nav.mega.modules.label")}
        </p>
        <Link
          to="/modules"
          onClick={close}
          className="inline-flex items-center gap-1.5 font-sans text-[13px] font-semibold text-[var(--accent)] no-underline hover:text-[var(--accent-hover)] focus:outline-none focus-visible:rounded-[4px] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        >
          {t("nav.mega.modules.overview")} <ArrowRight size={13} aria-hidden="true" />
        </Link>
      </div>
      <ul className="grid grid-cols-3 gap-2.5">
        {MODULE_TILES.map(({ key, Icon, ai }) => (
          <li key={key}>
            <Link
              to={`/modules/${key}`}
              onClick={close}
              className="flex h-full gap-3 rounded-[13px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4 no-underline transition-colors hover:border-[var(--border-default)] hover:bg-[var(--mkt-panel)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[var(--mkt-tint-accent)] text-[var(--accent-hover)]">
                <Icon size={19} aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="mb-1 flex items-center gap-1.5">
                  <span className="font-sans text-sm font-semibold text-[var(--fg-primary)]">
                    {t(`modules.items.${key}.name`)}
                  </span>
                  {ai && (
                    <span className="shrink-0 rounded-full bg-[var(--mkt-tint-accent)] px-1.5 py-0.5 font-sans text-[10px] font-semibold text-[var(--accent-hover)]">
                      {t("nav.mega.modules.ai_badge")}
                    </span>
                  )}
                </span>
                <span className="block font-serif text-[12.5px] leading-snug text-[var(--fg-muted)]">
                  {t(`modules.items.${key}.tagline`)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Resources panel: category links + featured strip ────────────────
function ResourcesPanel({ close }: { readonly close: () => void }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="w-[420px] rounded-[18px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-[22px] shadow-[var(--shadow-lg)]">
      <p className="mb-2.5 font-sans text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fg-muted)]">
        {t("nav.mega.resources.categories_label")}
      </p>
      <ul className="flex flex-col gap-0.5">
        {RESOURCE_LINKS.map(({ key, Icon, to }) => (
          <li key={key}>
            <Link to={to} onClick={close} className={PANEL_LINK_CLASS}>
              <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] bg-[var(--mkt-panel)] text-[var(--fg-secondary)]">
                <Icon size={14} aria-hidden="true" />
              </span>
              <span>
                <span className="block font-sans text-[13.5px] font-semibold text-[var(--fg-primary)]">
                  {t(`nav.mega.resources.links.${key}.title`)}
                </span>
                <span className="block font-serif text-[12px] leading-snug text-[var(--fg-muted)]">
                  {t(`nav.mega.resources.links.${key}.desc`)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <Link
        to="/ai"
        onClick={close}
        className="mt-4 flex items-center gap-3 rounded-[10px] bg-[var(--mkt-panel)] p-3 no-underline transition-colors hover:bg-[var(--bg-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[var(--mkt-tint-accent)] text-[var(--accent)]">
          <Sparkles size={17} aria-hidden="true" />
        </span>
        <span className="flex-1">
          <span className="block font-sans text-[13px] font-semibold text-[var(--fg-primary)]">
            {t("nav.mega.resources.featured.title")}
          </span>
          <span className="block font-serif text-[12.5px] text-[var(--fg-muted)]">
            {t("nav.mega.resources.featured.body")}
          </span>
        </span>
        <span className="shrink-0 rounded-[8px] bg-[var(--accent)] px-3 py-1.5 font-sans text-[12.5px] font-semibold text-[var(--fg-on-accent)]">
          {t("nav.mega.resources.featured.cta")}
        </span>
      </Link>
    </div>
  );
}

// ── Mobile disclosure items (the mega-menus are desktop-only; mobile lists
//    the same routes flat) ─────────────────────────────────────────────────
interface NavItem {
  readonly key: string;
  readonly labelKey: string;
  readonly to: string;
}
const MOBILE_ITEMS: readonly NavItem[] = [
  { key: "product", labelKey: "nav.items.product", to: "/product" },
  { key: "modules", labelKey: "nav.items.modules", to: "/modules" },
  { key: "ai", labelKey: "nav.items.ai", to: "/ai" },
  { key: "pricing", labelKey: "nav.items.pricing", to: "/pricing" },
  { key: "resources", labelKey: "nav.items.resources", to: "/help" },
];

export function MarketingNav(): React.ReactElement {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile sheet on Escape (matches mega-menu keyboard behavior).
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const plainLinkClass = ({ isActive }: { isActive: boolean }): string =>
    cn(
      "rounded-[8px] px-2.5 py-2 font-sans text-sm font-medium no-underline transition-colors",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
      isActive
        ? "bg-[var(--mkt-tint-accent)] text-[var(--accent-hover)]"
        : "text-[var(--fg-secondary)] hover:bg-[var(--mkt-panel)]",
    );

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-[1200px] items-center gap-7 px-6 py-3.5 lg:px-8">
        <Link
          to="/"
          aria-label={t("nav.home_label")}
          className="shrink-0 rounded-[8px] no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        >
          <Wordmark size={21} />
        </Link>

        {/* Desktop nav */}
        <nav aria-label={t("nav.primary_label")} className="hidden flex-1 items-center gap-1 lg:flex">
          <MegaMenu menuKey="product" label={t("nav.items.product")}>
            {(close): React.ReactNode => <ProductPanel close={close} />}
          </MegaMenu>
          <MegaMenu menuKey="modules" label={t("nav.items.modules")}>
            {(close): React.ReactNode => <ModulesPanel close={close} />}
          </MegaMenu>
          {/* Plain links (no dropdown) — mirror the mockup nav order */}
          <NavLink to="/ai" className={plainLinkClass}>
            {t("nav.items.ai")}
          </NavLink>
          <NavLink to="/pricing" className={plainLinkClass}>
            {t("nav.items.pricing")}
          </NavLink>
          <MegaMenu menuKey="resources" label={t("nav.items.resources")}>
            {(close): React.ReactNode => <ResourcesPanel close={close} />}
          </MegaMenu>
        </nav>

        <div className="ml-auto hidden items-center gap-4 lg:flex">
          {APP_LOGIN_URL !== undefined && (
            <a
              href={APP_LOGIN_URL}
              className="rounded-[6px] font-sans text-sm font-medium text-[var(--fg-primary)] no-underline hover:text-[var(--accent-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              {t("nav.login")}
            </a>
          )}
          <Button asChild variant="primary">
            <Link to={DEMO_REQUEST_PATH} className="no-underline">
              {t("nav.request_demo")}
            </Link>
          </Button>
        </div>

        {/* Mobile actions */}
        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <Button asChild variant="primary" size="sm">
            <Link to={DEMO_REQUEST_PATH} className="no-underline">
              {t("nav.request_demo")}
            </Link>
          </Button>
          <button
            type="button"
            onClick={(): void => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? t("nav.close_menu") : t("nav.open_menu")}
            className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-[var(--border-default)] bg-[var(--bg-raised)] text-[var(--fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          >
            {mobileOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile disclosure menu */}
      {mobileOpen && (
        <div className="border-t border-[var(--border-subtle)] bg-[var(--bg-raised)] px-6 py-3 lg:hidden">
          <nav aria-label={t("nav.primary_label")} className="flex flex-col gap-1">
            {MOBILE_ITEMS.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                onClick={(): void => setMobileOpen(false)}
                className={({ isActive }): string =>
                  cn(
                    "rounded-[10px] px-3 py-3 font-sans text-base font-medium no-underline",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
                    isActive
                      ? "bg-[var(--mkt-tint-accent)] text-[var(--accent-hover)]"
                      : "text-[var(--fg-secondary)] hover:bg-[var(--mkt-panel)]",
                  )
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>
          {APP_LOGIN_URL !== undefined && (
            <div className="mt-3 border-t border-[var(--border-subtle)] pt-3">
              <a
                href={APP_LOGIN_URL}
                className="block rounded-[10px] px-3 py-3 font-sans text-base font-medium text-[var(--fg-secondary)] no-underline hover:bg-[var(--mkt-panel)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              >
                {t("nav.login")}
              </a>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
